// src/components/Admin/IndustryTaxonomySettings.js
// Admin component for managing organization-specific industry taxonomy
// 
// This component allows firm admins to customize the industry dropdown
// options for their organization. Place this in FirmPortalAccount or
// a dedicated Settings section.

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Restore as RestoreIcon,
  Save as SaveIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { DEFAULT_INDUSTRY_TAXONOMY } from '../../utils/taxonomyUtils';
import { toast } from 'react-toastify';

/**
 * IndustryTaxonomySettings - Admin component for managing industry list
 * 
 * Features:
 * - View current taxonomy (org-specific or default)
 * - Add new industries
 * - Remove industries (with confirmation)
 * - Reorder industries (drag-drop or up/down buttons)
 * - Reset to system defaults
 * - Save changes to Firestore
 * 
 * Props:
 * @param {string} organizationId - The organization ID
 * @param {object} user - Current user object (for audit trail)
 */
const IndustryTaxonomySettings = ({ organizationId, user }) => {
  // State
  const [taxonomy, setTaxonomy] = useState([]);
  const [originalTaxonomy, setOriginalTaxonomy] = useState([]);
  const [isUsingDefault, setIsUsingDefault] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // New industry input
  const [newIndustry, setNewIndustry] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState('');
  
  // Dialogs
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [industryToDelete, setIndustryToDelete] = useState(null);

  // Load taxonomy on mount
  useEffect(() => {
    loadTaxonomy();
  }, [organizationId]);

  // Track changes
  useEffect(() => {
    const changed = JSON.stringify(taxonomy) !== JSON.stringify(originalTaxonomy);
    setHasChanges(changed);
  }, [taxonomy, originalTaxonomy]);

  const loadTaxonomy = async () => {
    if (!organizationId) {
      setTaxonomy([...DEFAULT_INDUSTRY_TAXONOMY]);
      setOriginalTaxonomy([...DEFAULT_INDUSTRY_TAXONOMY]);
      setIsUsingDefault(true);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const orgRef = doc(db, 'organizations', organizationId);
      const orgSnap = await getDoc(orgRef);

      if (orgSnap.exists()) {
        const orgData = orgSnap.data();
        if (orgData.industryTaxonomy && Array.isArray(orgData.industryTaxonomy) && orgData.industryTaxonomy.length > 0) {
          setTaxonomy([...orgData.industryTaxonomy]);
          setOriginalTaxonomy([...orgData.industryTaxonomy]);
          setIsUsingDefault(false);
        } else {
          setTaxonomy([...DEFAULT_INDUSTRY_TAXONOMY]);
          setOriginalTaxonomy([...DEFAULT_INDUSTRY_TAXONOMY]);
          setIsUsingDefault(true);
        }
      } else {
        setTaxonomy([...DEFAULT_INDUSTRY_TAXONOMY]);
        setOriginalTaxonomy([...DEFAULT_INDUSTRY_TAXONOMY]);
        setIsUsingDefault(true);
      }
    } catch (error) {
      console.error('Error loading taxonomy:', error);
      toast.error('Failed to load industry settings');
      setTaxonomy([...DEFAULT_INDUSTRY_TAXONOMY]);
      setOriginalTaxonomy([...DEFAULT_INDUSTRY_TAXONOMY]);
      setIsUsingDefault(true);
    } finally {
      setLoading(false);
    }
  };

  const handleAddIndustry = () => {
    const trimmed = newIndustry.trim();
    if (!trimmed) {
      toast.warning('Please enter an industry name');
      return;
    }
    
    // Check for duplicates (case-insensitive)
    if (taxonomy.some(t => t.toLowerCase() === trimmed.toLowerCase())) {
      toast.warning('This industry already exists');
      return;
    }

    // Add before "Other" if it exists, otherwise at end
    const otherIndex = taxonomy.indexOf('Other');
    if (otherIndex >= 0) {
      const newTaxonomy = [...taxonomy];
      newTaxonomy.splice(otherIndex, 0, trimmed);
      setTaxonomy(newTaxonomy);
    } else {
      setTaxonomy([...taxonomy, trimmed]);
    }
    
    setNewIndustry('');
    toast.success(`Added "${trimmed}"`);
  };

  const handleDeleteIndustry = (index) => {
    const industryName = taxonomy[index];
    
    // Don't allow deleting "Other"
    if (industryName === 'Other') {
      toast.warning('"Other" cannot be removed - it\'s required for custom entries');
      return;
    }

    setIndustryToDelete({ index, name: industryName });
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (industryToDelete) {
      const newTaxonomy = taxonomy.filter((_, i) => i !== industryToDelete.index);
      setTaxonomy(newTaxonomy);
      toast.success(`Removed "${industryToDelete.name}"`);
    }
    setDeleteConfirmOpen(false);
    setIndustryToDelete(null);
  };

  const handleEditIndustry = (index) => {
    if (taxonomy[index] === 'Other') {
      toast.warning('"Other" cannot be renamed');
      return;
    }
    setEditingIndex(index);
    setEditValue(taxonomy[index]);
  };

  const handleSaveEdit = () => {
    const trimmed = editValue.trim();
    if (!trimmed) {
      toast.warning('Industry name cannot be empty');
      return;
    }
    
    // Check for duplicates
    if (taxonomy.some((t, i) => i !== editingIndex && t.toLowerCase() === trimmed.toLowerCase())) {
      toast.warning('This industry already exists');
      return;
    }

    const newTaxonomy = [...taxonomy];
    newTaxonomy[editingIndex] = trimmed;
    setTaxonomy(newTaxonomy);
    setEditingIndex(null);
    setEditValue('');
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditValue('');
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newTaxonomy = [...taxonomy];
    [newTaxonomy[index - 1], newTaxonomy[index]] = [newTaxonomy[index], newTaxonomy[index - 1]];
    setTaxonomy(newTaxonomy);
  };

  const handleMoveDown = (index) => {
    if (index === taxonomy.length - 1) return;
    const newTaxonomy = [...taxonomy];
    [newTaxonomy[index], newTaxonomy[index + 1]] = [newTaxonomy[index + 1], newTaxonomy[index]];
    setTaxonomy(newTaxonomy);
  };

  const handleResetToDefaults = () => {
    setTaxonomy([...DEFAULT_INDUSTRY_TAXONOMY]);
    setResetDialogOpen(false);
    toast.info('Reset to system defaults - remember to save');
  };

  const handleSave = async () => {
    if (!organizationId) {
      toast.error('No organization ID - cannot save');
      return;
    }

    try {
      setSaving(true);
      const orgRef = doc(db, 'organizations', organizationId);
      
      await updateDoc(orgRef, {
        industryTaxonomy: taxonomy,
        industryTaxonomyUpdatedAt: serverTimestamp(),
        industryTaxonomyUpdatedBy: user?.email || 'Unknown'
      });

      setOriginalTaxonomy([...taxonomy]);
      setIsUsingDefault(false);
      toast.success('Industry taxonomy saved successfully');
    } catch (error) {
      console.error('Error saving taxonomy:', error);
      toast.error('Failed to save industry settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setTaxonomy([...originalTaxonomy]);
    setNewIndustry('');
    setEditingIndex(null);
    toast.info('Changes discarded');
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <CircularProgress size={24} />
          <Typography>Loading industry settings...</Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Industry Taxonomy
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Customize the industry options available when creating clients.
            {isUsingDefault && !hasChanges && (
              <Chip 
                label="Using System Defaults" 
                size="small" 
                sx={{ ml: 1 }} 
                color="info"
              />
            )}
          </Typography>
        </Box>
        
        <Box display="flex" gap={1}>
          <Tooltip title="Reset to system defaults">
            <Button
              variant="outlined"
              size="small"
              startIcon={<RestoreIcon />}
              onClick={() => setResetDialogOpen(true)}
            >
              Reset
            </Button>
          </Tooltip>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Add new industry */}
      <Box display="flex" gap={1} mb={3}>
        <TextField
          size="small"
          placeholder="Add new industry..."
          value={newIndustry}
          onChange={(e) => setNewIndustry(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleAddIndustry()}
          sx={{ flexGrow: 1 }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddIndustry}
          disabled={!newIndustry.trim()}
        >
          Add
        </Button>
      </Box>

      {/* Industry list */}
      <List dense sx={{ bgcolor: 'grey.50', borderRadius: 1, maxHeight: 400, overflow: 'auto' }}>
        {taxonomy.map((industry, index) => (
          <ListItem
            key={`${industry}-${index}`}
            sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: industry === 'Other' ? 'grey.100' : 'inherit',
              '&:last-child': { borderBottom: 'none' }
            }}
          >
            <DragIcon sx={{ mr: 1, color: 'text.disabled', cursor: 'grab' }} />
            
            {editingIndex === index ? (
              <TextField
                size="small"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSaveEdit()}
                onBlur={handleSaveEdit}
                autoFocus
                sx={{ flexGrow: 1, mr: 1 }}
              />
            ) : (
              <ListItemText 
                primary={industry}
                secondary={industry === 'Other' ? 'Required - allows custom entries' : null}
              />
            )}

            <ListItemSecondaryAction>
              <Box display="flex" gap={0.5}>
                {editingIndex === index ? (
                  <>
                    <IconButton size="small" onClick={handleSaveEdit} color="primary">
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={handleCancelEdit}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <Tooltip title="Move up">
                      <span>
                        <IconButton 
                          size="small" 
                          onClick={() => handleMoveUp(index)}
                          disabled={index === 0}
                        >
                          ↑
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move down">
                      <span>
                        <IconButton 
                          size="small" 
                          onClick={() => handleMoveDown(index)}
                          disabled={index === taxonomy.length - 1}
                        >
                          ↓
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton 
                        size="small" 
                        onClick={() => handleEditIndustry(index)}
                        disabled={industry === 'Other'}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton 
                        size="small" 
                        onClick={() => handleDeleteIndustry(index)}
                        disabled={industry === 'Other'}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        {taxonomy.length} industries configured
      </Typography>

      {/* Save/Discard buttons */}
      {hasChanges && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
            <Typography variant="body2">
              You have unsaved changes
            </Typography>
            <Box display="flex" gap={1}>
              <Button 
                size="small" 
                onClick={handleDiscard}
                disabled={saving}
              >
                Discard
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </Box>
        </Alert>
      )}

      {/* Reset confirmation dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>Reset to System Defaults?</DialogTitle>
        <DialogContent>
          <Typography>
            This will replace your custom industry list with the system defaults.
            You'll need to click "Save Changes" to apply.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            System defaults: {DEFAULT_INDUSTRY_TAXONOMY.length} industries
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleResetToDefaults} color="warning" variant="contained">
            Reset
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Remove Industry?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove "{industryToDelete?.name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Existing clients with this industry will keep their current value,
            but it won't appear in the dropdown for new clients.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default IndustryTaxonomySettings;
